import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api";
import {
  FILTER_OPERATORS,
  SEARCHABLE_FIELDS,
  DEFAULT_SELECT_FIELDS,
  PAGINATION_DEFAULTS,
  TOAST_MESSAGES,
  SORT_OPTIONS,
} from "../../constants/filters";

/**
 * Custom hook for advanced lead filtering
 * Provides all functionality needed for sophisticated smart filters
 * Aligned with CLIENT_IMPLEMENTATION_GUIDE structure
 */
export const useSmartFilter = () => {
  // Filter state matching CLIENT_IMPLEMENTATION_GUIDE structure
  const [filterState, setFilterState] = useState({
    filters: [],
    sort: SORT_OPTIONS.CREATED_AT_DESC,
    pagination: {
      page: PAGINATION_DEFAULTS.PAGE,
      limit: PAGINATION_DEFAULTS.LIMIT,
    },
    select: DEFAULT_SELECT_FIELDS,
  });

  // Additional state for UI convenience (quick filters)
  const [quickFilters, setQuickFilters] = useState({
    booleanFilters: {},
    dropdownFilters: {},
    stringFilters: {},
    numberFilters: {},
    dateFilters: {},
  });

  // Data state
  const [leads, setLeads] = useState([]);
  const [leadsVersion, setLeadsVersion] = useState(0); // Force re-render trigger
  const [filterOptions, setFilterOptions] = useState({});
  const [filterMetadata, setFilterMetadata] = useState({});
  const [filterStats, setFilterStats] = useState({});
  const [filterPresets, setFilterPresets] = useState([]);
  const [adminNames, setAdminNames] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: PAGINATION_DEFAULTS.PAGE,
    limit: PAGINATION_DEFAULTS.LIMIT,
    total: 0,
    totalPages: 0,
  });

  // Global search
  const [globalSearch, setGlobalSearch] = useState("");

  /**
   * Initialize filter data on component mount
   */
  useEffect(() => {
    const initializeFilters = async () => {
      try {
        setLoading(true);
        const [optionsRes, metadataRes, presetsRes, adminRes] =
          await Promise.all([
            api.getLeadFilterOptions(),
            api.getFilterMetadata(),
            api.getFilterPresets(),
            api.getAdminNames(),
          ]);

        setFilterOptions(optionsRes.data);
        setFilterMetadata(metadataRes.data);
        setFilterPresets(presetsRes.data);
        setAdminNames(adminRes.data);
      } catch (err) {
        const errorMessage = TOAST_MESSAGES.INIT_FAILED(err.message);
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    initializeFilters();
  }, []);

  /**
   * Fetch leads with current filters
   */
  const fetchFilteredLeads = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setError(null);

        let finalFilters = [...filterState.filters];

        // Add global search as OR conditions across searchable text fields
        if (globalSearch && globalSearch.trim()) {
          const globalSearchFilters = SEARCHABLE_FIELDS.map((field) => ({
            field,
            operator: FILTER_OPERATORS.CONTAINS,
            value: globalSearch.trim(),
            logic: FILTER_OPERATORS.OR,
          }));

          finalFilters = [...finalFilters, ...globalSearchFilters];
        }

        const updatedFilterState = {
          ...filterState,
          filters: finalFilters,
          pagination: { ...filterState.pagination, page },
        };

        const response = await api.getLeadsWithAdvancedFilter(
          updatedFilterState
        );

        // Clear existing leads first to ensure clean state
        setLeads([]);

        // Then set new leads - force new array reference and update version to trigger re-render
        setTimeout(() => {
          setLeads([...response.data]);
          setLeadsVersion((prev) => prev + 1);

          setPagination({
            page: response.pagination.page,
            limit: response.pagination.limit,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
          });
          setFilterStats(response.appliedFilters || {});
        }, 10);
      } catch (err) {
        const errorMessage = TOAST_MESSAGES.FETCH_FAILED(err.message);
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [filterState, globalSearch]
  );

  /**
   * Add a filter to the filterState
   */
  const addFilter = (field, operator, value, logicalOperator = "AND") => {
    if (!field || !operator || value === undefined || value === null) {
      toast.error(TOAST_MESSAGES.INVALID_FILTER);
      return;
    }

    const newFilter = {
      field,
      operator,
      value,
      logic: logicalOperator,
    };

    setFilterState((prev) => ({
      ...prev,
      filters: [...prev.filters, newFilter],
      pagination: { ...prev.pagination, page: PAGINATION_DEFAULTS.PAGE },
    }));

    toast.success(TOAST_MESSAGES.FILTER_ADDED(field, operator, value));

    // Trigger fetch with debounce
    triggerFilteredFetch();
  };

  /**
   * Remove a filter by index
   */
  const removeFilter = (index) => {
    if (index < 0 || index >= filterState.filters.length) {
      toast.error(TOAST_MESSAGES.INVALID_INDEX);
      return;
    }

    setFilterState((prev) => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index),
      pagination: { ...prev.pagination, page: PAGINATION_DEFAULTS.PAGE },
    }));

    toast.success(TOAST_MESSAGES.FILTER_REMOVED);

    // Trigger fetch with debounce
    triggerFilteredFetch();
  };

  // Debounce timer for filter updates
  const debounceTimer = useRef(null);

  // Helper function to trigger filtered fetch with debounce
  const triggerFilteredFetch = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      fetchFilteredLeads(1);
    }, 300); // 300ms debounce
  }, [fetchFilteredLeads]);

  /**
   * Update boolean filter (convenience method)
   */
  const updateBooleanFilter = (field, value) => {
    // Update filter state in a single operation to avoid race conditions
    setFilterState((prev) => {
      // Remove existing filter for this field
      let newFilters = prev.filters.filter((f) => f.field !== field);

      // Add new filter if value is not 'all'
      if (value !== "all") {
        newFilters.push({
          field,
          operator: FILTER_OPERATORS.EQUALS,
          value: value === "yes",
          logic: FILTER_OPERATORS.AND,
        });
      }

      const newState = {
        ...prev,
        filters: newFilters,
        pagination: { ...prev.pagination, page: 1 }, // Reset to first page
      };

      return newState;
    });

    // Update quick filters for UI
    setQuickFilters((prev) => ({
      ...prev,
      booleanFilters: {
        ...prev.booleanFilters,
        [field]: value,
      },
    }));

    // Trigger fetch with debounce
    triggerFilteredFetch();
  };

  /**
   * Update dropdown filter (convenience method)
   */
  const updateDropdownFilter = (field, values) => {
    // Update filter state in a single operation to avoid race conditions
    setFilterState((prev) => {
      // Remove existing filter for this field
      let newFilters = prev.filters.filter((f) => f.field !== field);

      // Add new filter if values are selected
      if (values && values.length > 0) {
        const operator =
          values.length > 1 ? FILTER_OPERATORS.IN : FILTER_OPERATORS.EQUALS;
        const value = values.length > 1 ? values : values[0];
        newFilters.push({
          field,
          operator,
          value,
          logic: FILTER_OPERATORS.AND,
        });
      }

      return {
        ...prev,
        filters: newFilters,
        pagination: { ...prev.pagination, page: 1 }, // Reset to first page
      };
    });

    // Update quick filters for UI
    setQuickFilters((prev) => ({
      ...prev,
      dropdownFilters: {
        ...prev.dropdownFilters,
        [field]: Array.isArray(values) ? values : [values],
      },
    }));

    // Trigger fetch with debounce
    triggerFilteredFetch();
  };

  /**
   * Update string filter (convenience method)
   */
  const updateStringFilter = (field, value, operator = "contains") => {
    // Update filter state in a single operation to avoid race conditions
    setFilterState((prev) => {
      // Remove existing filter for this field
      let newFilters = prev.filters.filter((f) => f.field !== field);

      // Add new filter if value is provided
      if (value && value.trim()) {
        newFilters.push({
          field,
          operator,
          value,
          logic: FILTER_OPERATORS.AND,
        });
      }

      return {
        ...prev,
        filters: newFilters,
        pagination: { ...prev.pagination, page: 1 }, // Reset to first page
      };
    });

    // Update quick filters for UI
    if (!value || !value.trim()) {
      setQuickFilters((prev) => {
        const newStringFilters = { ...prev.stringFilters };
        delete newStringFilters[field];
        return {
          ...prev,
          stringFilters: newStringFilters,
        };
      });
    } else {
      setQuickFilters((prev) => ({
        ...prev,
        stringFilters: {
          ...prev.stringFilters,
          [field]: { value, operator },
        },
      }));
    }

    // Trigger fetch with debounce
    triggerFilteredFetch();
  };

  /**
   * Update number range filter (convenience method)
   */
  const updateNumberFilter = (field, min = null, max = null) => {
    // Update filter state in a single operation to avoid race conditions
    setFilterState((prev) => {
      // Remove existing filters for this field (both min and max)
      let newFilters = prev.filters.filter((f) => f.field !== field);

      // Add filters based on min/max values
      if (min !== null && min !== "") {
        newFilters.push({
          field,
          operator: FILTER_OPERATORS.GTE,
          value: Number(min),
          logic: FILTER_OPERATORS.AND,
        });
      }
      if (max !== null && max !== "") {
        newFilters.push({
          field,
          operator: FILTER_OPERATORS.LTE,
          value: Number(max),
          logic: FILTER_OPERATORS.AND,
        });
      }

      return {
        ...prev,
        filters: newFilters,
        pagination: { ...prev.pagination, page: 1 }, // Reset to first page
      };
    });

    // Update quick filters for UI
    if ((min === null || min === "") && (max === null || max === "")) {
      setQuickFilters((prev) => {
        const newNumberFilters = { ...prev.numberFilters };
        delete newNumberFilters[field];
        return {
          ...prev,
          numberFilters: newNumberFilters,
        };
      });
    } else {
      setQuickFilters((prev) => ({
        ...prev,
        numberFilters: {
          ...prev.numberFilters,
          [field]: { min, max },
        },
      }));
    }
  };

  /**
   * Update date range filter (convenience method)
   */
  const updateDateFilter = (field, startDate = null, endDate = null) => {
    // Update filter state in a single operation to avoid race conditions
    setFilterState((prev) => {
      // Remove existing filters for this field
      let newFilters = prev.filters.filter((f) => f.field !== field);

      // Add filters based on date values
      if (startDate) {
        newFilters.push({
          field,
          operator: FILTER_OPERATORS.GTE,
          value: new Date(startDate).toISOString(),
          logic: FILTER_OPERATORS.AND,
        });
      }
      if (endDate) {
        newFilters.push({
          field,
          operator: FILTER_OPERATORS.LTE,
          value: new Date(endDate).toISOString(),
          logic: FILTER_OPERATORS.AND,
        });
      }

      return {
        ...prev,
        filters: newFilters,
        pagination: { ...prev.pagination, page: 1 }, // Reset to first page
      };
    });

    // Update quick filters for UI
    if (!startDate && !endDate) {
      setQuickFilters((prev) => {
        const newDateFilters = { ...prev.dateFilters };
        delete newDateFilters[field];
        return {
          ...prev,
          dateFilters: newDateFilters,
        };
      });
    } else {
      setQuickFilters((prev) => ({
        ...prev,
        dateFilters: {
          ...prev.dateFilters,
          [field]: { startDate, endDate },
        },
      }));
    }
  };

  /**
   * Clear all filters
   */
  const clearAllFilters = () => {
    setFilterState((prev) => ({
      ...prev,
      filters: [],
      pagination: { ...prev.pagination, page: PAGINATION_DEFAULTS.PAGE },
    }));
    setQuickFilters({
      booleanFilters: {},
      dropdownFilters: {},
      stringFilters: {},
      numberFilters: {},
      dateFilters: {},
    });
    setGlobalSearch("");
    toast.success(TOAST_MESSAGES.ALL_FILTERS_CLEARED);
  };

  /**
   * Clear specific filter type
   */
  const clearFilterType = (type) => {
    setQuickFilters((prev) => ({
      ...prev,
      [type]: {},
    }));
  };

  /**
   * Apply a saved filter preset
   */
  const applyFilterPreset = (preset) => {
    if (!preset || !preset.filters) {
      toast.error(TOAST_MESSAGES.INVALID_PRESET);
      return;
    }

    setFilterState((prev) => ({
      ...prev,
      filters: preset.filters || [],
      pagination: { ...prev.pagination, page: PAGINATION_DEFAULTS.PAGE },
    }));

    toast.success(TOAST_MESSAGES.PRESET_APPLIED(preset.name));
  };

  /**
   * Save current filters as a preset
   */
  const saveFilterPreset = async (name, description, isPublic = false) => {
    if (!name || !name.trim()) {
      toast.error(TOAST_MESSAGES.PRESET_NAME_REQUIRED);
      throw new Error(TOAST_MESSAGES.PRESET_NAME_REQUIRED);
    }

    try {
      const presetData = {
        name: name.trim(),
        description: description?.trim() || "",
        filters: filterState.filters,
        isPublic,
      };

      const response = await api.saveFilterPreset(presetData);
      setFilterPresets((prev) => [...prev, response.data]);

      toast.success(TOAST_MESSAGES.PRESET_SAVED(name));
      return response.data;
    } catch (err) {
      const errorMessage = TOAST_MESSAGES.SAVE_FAILED(err.message);
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  };

  /**
   * Delete a filter preset
   */
  const deleteFilterPreset = async (presetId) => {
    if (!presetId) {
      toast.error(TOAST_MESSAGES.PRESET_ID_REQUIRED);
      throw new Error(TOAST_MESSAGES.PRESET_ID_REQUIRED);
    }

    try {
      await api.deleteFilterPreset(presetId);
      setFilterPresets((prev) =>
        prev.filter((preset) => preset._id !== presetId)
      );

      toast.success(TOAST_MESSAGES.PRESET_DELETED);
    } catch (err) {
      const errorMessage = TOAST_MESSAGES.DELETE_FAILED(err.message);
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  };

  /**
   * Get statistics for current filters
   */
  const getFilterStats = async () => {
    try {
      const response = await api.getFilteredLeadsStats(filterState);
      setFilterStats(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = TOAST_MESSAGES.STATS_FAILED(err.message);
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  };

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = () => {
    return filterState.filters.length > 0 || globalSearch.length > 0;
  };

  /**
   * Get count of active filters
   */
  const getActiveFilterCount = () => {
    return filterState.filters.length + (globalSearch ? 1 : 0);
  };

  /**
   * Pagination helpers
   */
  const goToPage = (page) => {
    fetchFilteredLeads(page);
  };

  const changePageSize = useCallback(
    async (newLimit) => {
      try {
        setLoading(true);
        setError(null);

        // Create the new filter state with updated page size
        const newFilterState = {
          ...filterState,
          pagination: {
            ...filterState.pagination,
            limit: newLimit,
            page: PAGINATION_DEFAULTS.PAGE, // Reset to first page
          },
        };

        // Add global search filters if active
        let finalFilters = [...newFilterState.filters];
        if (globalSearch && globalSearch.trim()) {
          const globalSearchFilters = SEARCHABLE_FIELDS.map((field) => ({
            field,
            operator: FILTER_OPERATORS.CONTAINS,
            value: globalSearch.trim(),
            logic: FILTER_OPERATORS.OR,
          }));
          finalFilters = [...finalFilters, ...globalSearchFilters];
        }

        const requestState = {
          ...newFilterState,
          filters: finalFilters,
        };

        // Make API call with new page size
        const response = await api.getLeadsWithAdvancedFilter(requestState);

        // Update all states
        setFilterState(newFilterState);
        setLeads(response.data || []);
        setLeadsVersion((prev) => prev + 1);
        setPagination({
          page: newFilterState.pagination.page,
          limit: newFilterState.pagination.limit,
          total: response.pagination?.total || 0,
          totalPages: Math.ceil(
            (response.pagination?.total || 0) / newFilterState.pagination.limit
          ),
        });

        // Update filter stats if available
        if (response.filterStats) {
          setFilterStats(response.filterStats);
        }
      } catch (err) {
        const errorMessage = TOAST_MESSAGES.FILTER_FAILED(err.message);
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [filterState, globalSearch]
  );

  // Track if initial fetch has happened
  const initialFetchDone = useRef(false);

  // Initial data fetch effect - only run once when metadata is loaded
  useEffect(() => {
    if (Object.keys(filterMetadata).length > 0 && !initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchFilteredLeads(1);
    }
  }, [filterMetadata, fetchFilteredLeads]);

  // Wrapper for global search that triggers fetch
  const updateGlobalSearch = useCallback(
    (searchValue) => {
      setGlobalSearch(searchValue);
      triggerFilteredFetch();
    },
    [triggerFilteredFetch]
  );

  // Optimistic update function for immediate UI updates
  const updateLeadInList = useCallback((updatedLead) => {
    setLeads((prevLeads) => {
      return prevLeads.map((lead) =>
        lead.id === updatedLead.id ? { ...lead, ...updatedLead } : lead
      );
    });
    // Increment version to force table re-render
    setLeadsVersion((prev) => prev + 1);
  }, []);

  return {
    // Data
    leads,
    leadsVersion, // For forcing re-renders if needed
    filterOptions,
    filterMetadata,
    filterStats,
    filterPresets,
    adminNames,

    // Filter state
    filterState,
    quickFilters,
    globalSearch,
    setGlobalSearch: updateGlobalSearch, // Use wrapped version

    // UI state
    loading,
    error,
    pagination,

    // Core filter actions (CLIENT_IMPLEMENTATION_GUIDE compatible)
    addFilter,
    removeFilter,

    // Convenience filter actions
    updateBooleanFilter,
    updateDropdownFilter,
    updateStringFilter,
    updateNumberFilter,
    updateDateFilter,
    clearAllFilters,
    clearFilterType,

    // Preset actions
    applyFilterPreset,
    saveFilterPreset,
    deleteFilterPreset,

    // Utility actions
    getFilterStats,
    hasActiveFilters,
    getActiveFilterCount,

    // Pagination
    goToPage,
    changePageSize,
    fetchFilteredLeads,

    // Optimistic updates
    updateLeadInList,
  };
};
